import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import translations from "../i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "ira_language";
const SUPPORTED_LANGUAGES = ["en", "uk"];

const resolveInitialLanguage = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  const browserLanguage = navigator.language?.toLowerCase().startsWith("uk") ? "uk" : "en";
  return browserLanguage;
};

const getTranslationByKey = (dictionary, key) =>
  key.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), dictionary);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(resolveInitialLanguage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => {
    const t = (key) => {
      const selectedDictionary = translations[language] || translations.en;
      return getTranslationByKey(selectedDictionary, key) ?? getTranslationByKey(translations.en, key) ?? key;
    };

    const switchLanguage = (nextLanguage) => {
      if (SUPPORTED_LANGUAGES.includes(nextLanguage)) {
        setLanguage(nextLanguage);
      }
    };

    return {
      language,
      switchLanguage,
      t,
    };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

